// frontend/components/sidebar/SidebarTreeItem.tsx

"use client";

import { FileText, MoreHorizontal, Plus } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import * as Lucide from "lucide-react";
import { useSidebarContext } from "../../contexts/SidebarContext";
import { checkEditable, checkIsTrueOwner } from "../../utils/documentUtils";
import { getTwemojiSvgUrl } from "../../utils/emoji";
import SidebarTree from "./SidebarTree";

interface SidebarTreeItemProps {
  doc: any;
  level: number;
  section: string;
}

// 개별 트리 노드 렌더링 및 조작 핸들링
export default function SidebarTreeItem({ doc, level, section }: SidebarTreeItemProps) {
  const router = useRouter();
  const [isDragging, setIsDragging] = useState(false);
  const [isIconHovered, setIsIconHovered] = useState(false);
  const [staticIconSrc, setStaticIconSrc] = useState<string | null>(null);

  const {
    docs, user, expandedDocs, setExpandedDocs, dropTarget,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleReorder,
    handleContextMenu, openCreateModal, colorTheme
  } = useSidebarContext();

  const hasChildren = docs.some((d: any) => d.parentId === doc.id);

  const nodeKey = `${section}-${doc.id}`;
  const isExpanded = expandedDocs[nodeKey];

  const canEditDoc = checkEditable(doc, user);
  const isTrueOwner = checkIsTrueOwner(doc, user);

  const isNeutral = colorTheme === "neutral";

  const [showRedDot, setShowRedDot] = useState(false);

  useEffect(() => {
    if (!user?.id || !doc.id) return;

    const checkReadStatus = () => {
      const lastViewedStr = localStorage.getItem(`alpha-doc-last-viewed-${user.id}-${doc.id}`);
      if (!lastViewedStr) {
        setShowRedDot(false);
        return;
      }
      const lastViewed = parseInt(lastViewedStr, 10);
      const updatedTime = new Date(doc.updatedAt).getTime();
      
      const isUpdatedBySelf = doc.updatedBy === user.name || doc.creatorId === user.id;
      if (updatedTime > lastViewed && !isUpdatedBySelf) {
        setShowRedDot(true);
      } else {
        setShowRedDot(false);
      }
    };

    checkReadStatus();

    const handleDocViewed = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.docId === doc.id) {
        setShowRedDot(false);
      }
    };

    window.addEventListener("doc-viewed", handleDocViewed);
    return () => window.removeEventListener("doc-viewed", handleDocViewed);
  }, [doc.id, doc.updatedAt, doc.updatedBy, doc.creatorId, user?.id, user?.name]);

  // 작성 주체 추출 로직 (백엔드 응답에 requester 필드가 없을 경우 원본 부서 폴백)
  const badgeText = doc.requesterDept || doc.requesterRole || (doc.accessDept ? doc.accessDept.split(',')[0].trim() : (doc.workspaceType === "PERSONAL" ? "개인" : "관리자"));

  // 움직이는 이미지(GIF, WebP)의 경우 캔버스를 이용해 첫 프레임만 추출하여 정지 이미지(PNG)로 변환
  useEffect(() => {
    if (doc.icon && (doc.icon.startsWith("upload:data:image/gif") || doc.icon.startsWith("upload:data:image/webp"))) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          // drawImage는 애니메이션 이미지의 첫 프레임만 캔버스에 그립니다.
          ctx.drawImage(img, 0, 0);
          setStaticIconSrc(canvas.toDataURL("image/png"));
        }
      };
      img.src = doc.icon.replace("upload:", "");
    } else {
      setStaticIconSrc(null);
    }
  }, [doc.icon]);

  const isDropBefore = dropTarget !== null && dropTarget.id === doc.id && dropTarget.where === "before";
  const isDropAfter  = dropTarget !== null && dropTarget.id === doc.id && dropTarget.where === "after";
  const isDragOver   = dropTarget !== null && dropTarget.id === doc.id && dropTarget.where === "inside";

  // 드래그 시작 이벤트 처리
  const onDragStart = (e: React.DragEvent) => {
    if (!isTrueOwner) {
      e.preventDefault();
      return alert("문서의 이동은 최초 작성팀만 가능합니다.");
    }
    setIsDragging(true);
    handleDragStart(e, doc.id);
  };

  const onDragEnd = () => setIsDragging(false);

  // 3존 감지: 상단 25% = before, 하단 25% = after, 중간 = inside(자식으로)
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging) { e.dataTransfer.dropEffect = "none"; return; }

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    const where = y < h * 0.25 ? "before" : y > h * 0.75 ? "after" : "inside";
    handleDragOver(e, doc.id, where);
  };

  // 드롭: before/after → 재정렬, inside → 기존 자식으로 이동
  const onDrop = (e: React.DragEvent) => {
    e.stopPropagation();
    const where = (dropTarget !== null && dropTarget.id === doc.id) ? dropTarget.where : "inside";
    if (where === "inside") {
      handleDrop(e, doc.id, doc.workspaceType);
    } else {
      handleReorder(e, doc.id, where as "before" | "after");
    }
  };

  // READ 권한만 있어도 즐겨찾기/링크 복사/공유 종료 같은 항목은 사용할 수 있어야 하므로
  // canEditDoc로 메뉴 자체를 막지 않음 — 편집 관련 항목은 ContextMenu 내부에서 개별적으로 비활성화됨
  const onContextMenu = (e: React.MouseEvent) => {
    handleContextMenu(
      e,
      doc.id,
      doc.title,
      doc.icon,
      isTrueOwner,
      section as "recent" | "favorite" | "team" | "personal",
      doc.isFavorite,
      !doc.parentId,
      doc.updatedBy,
      doc.updatedAt,
      doc.permLevel
    );
  };

  // 하위 트리 확장 및 라우팅 처리
  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedDocs(p => ({ ...p, [nodeKey]: !p[nodeKey] }));
  };

  const navigateToDoc = () => {
    if (user?.id) {
      localStorage.setItem(`alpha-doc-last-viewed-${user.id}-${doc.id}`, String(Date.now()));
      window.dispatchEvent(new CustomEvent("doc-viewed", { detail: { docId: doc.id } }));
    }
    router.push(`/doc/${doc.id}`);
  };

  // 하위 문서 추가 모달 호출
  const onAddChild = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openCreateModal(doc.workspaceType as "TEAM" | "PERSONAL", doc.id, doc.title);
  };

  const renderIcon = (iconString: string | null) => {
    if (iconString === "transparent") return <div className="w-4.5 h-4.5" />;
    if (!iconString) return <FileText size={18} className={isNeutral ? 'text-gray-400 dark:text-gray-500' : 'text-white/80'} />;

    if (iconString.startsWith("emoji:")) {
      const code = iconString.replace("emoji:", "");
      if (/^[0-9a-f\-]+$/.test(code)) {
        // DocHeader와 동일하게 개별 이미지 파일 로드
        return <img src={getTwemojiSvgUrl(code)} alt="emoji" className="w-5 h-5 object-contain" />;
      }
      return <span className="text-lg leading-none">{code}</span>;
    }

    if (iconString.startsWith("lucide:")) {
      const parts = iconString.replace("lucide:", "").split(":");
      const iconName = parts[0];
      const colorHex = parts[1] || "currentColor";
      const IconComponent = (Lucide as any)[iconName];

      if (!IconComponent) return <FileText size={18} className={isNeutral ? 'text-gray-400 dark:text-gray-500' : 'text-white/80'} />;
      return <IconComponent size={18} strokeWidth={1.5} color={colorHex !== "currentColor" ? colorHex : undefined} className={colorHex === "currentColor" ? (isNeutral ? 'text-gray-700 dark:text-gray-200' : 'text-white/90') : ""} />;
    }

    if (iconString.startsWith("upload:")) {
      const src = staticIconSrc || iconString.replace("upload:", "");
      return <img src={src} alt="custom" className="w-5 h-5 object-contain rounded-sm" />;
    }

    return <span className="text-lg leading-none">{iconString}</span>;
  };

  return (
    <div className={`relative ${isDragging ? 'opacity-40' : ''}`}>
      {/* before 드롭 라인 */}
      {isDropBefore && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5 bg-[#2EAADC] z-10 rounded-full"
          style={{ left: `${(level * 16) + 8}px` }}
        />
      )}
      <div
        draggable={isTrueOwner}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDragLeave={handleDragLeave}
        onDrop={onDrop}
        onContextMenu={onContextMenu}
        className={`group flex items-center justify-between text-[13px] rounded cursor-pointer transition-colors py-1 pr-2 ${
          isDragOver
            ? 'bg-[#2EAADC]/40 text-white'
            : (isDropBefore || isDropAfter)
              ? (isNeutral ? 'bg-gray-100 dark:bg-white/5' : 'bg-white/5')
              : (isNeutral ? 'hover:bg-gray-200 dark:hover:bg-white/10' : 'hover:bg-white/10')
        }`}
        style={{ paddingLeft: `${(level * 16) + 8}px` }}
      >
        <div className="flex items-center flex-1 min-w-0" onClick={navigateToDoc}>
          <div
            className="relative w-6 h-6 flex items-center justify-center shrink-0"
            onMouseEnter={() => setIsIconHovered(true)}
            onMouseLeave={() => setIsIconHovered(false)}
          >
            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${hasChildren && isIconHovered ? 'opacity-0' : 'opacity-100'}`}>
              {renderIcon(doc.icon)}
            </div>

            {hasChildren && (
              <button
                onClick={toggleExpand}
                className={`absolute inset-0 flex items-center justify-center rounded transition-all duration-200 ${isIconHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-75'} ${isNeutral ? 'hover:bg-gray-300 dark:hover:bg-white/20' : 'hover:bg-white/20'}`}
              >
                <span className={`text-[12px] font-bold font-mono transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                  {'>'}
                </span>
              </button>
            )}
          </div>

          <span className={`truncate pl-1.5 font-medium ${isNeutral ? 'text-gray-700 dark:text-gray-200' : 'text-white/90'}`}>{doc.title || "새 페이지"}</span>
          {showRedDot && (
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0 ml-1.5 animate-pulse" title="새로운 변경사항 또는 댓글이 있습니다" />
          )}
        </div>

        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity pl-2 shrink-0">
          {/* ADMIN은 모든 팀 문서에 출처 배지 표시. 일반 사용자는 "팀 스페이스" 섹션에서
              본인이 이동시킬 수 없는(최초 작성팀이 아닌) 문서에 한해 어느 팀 문서인지 표기 */}
          {(user?.role === "ADMIN" || (section === "team" && !isTrueOwner)) && badgeText && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded ml-auto mr-1 ${isNeutral ? 'bg-gray-200 text-gray-500 dark:bg-black/40 dark:text-gray-400' : 'bg-black/20 text-white/80'}`}>
              {badgeText}
            </span>
          )}
          {canEditDoc && (
            <>
              <button onClick={onContextMenu} className={`p-0.5 rounded transition-colors ${isNeutral ? 'hover:bg-gray-200 dark:hover:bg-zinc-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200' : 'hover:bg-white/20 text-white/70 hover:text-white'}`} title="메뉴 열기">
                <MoreHorizontal size={14} />
              </button>
              {canEditDoc && (
                <button onClick={onAddChild} className={`p-0.5 rounded transition-colors ${isNeutral ? 'hover:bg-gray-200 dark:hover:bg-zinc-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200' : 'hover:bg-white/20 text-white/70 hover:text-white'}`} title="하위 페이지 추가">
                  <Plus size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* after 드롭 라인 */}
      {isDropAfter && (
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2EAADC] z-10 rounded-full"
          style={{ left: `${(level * 16) + 8}px` }}
        />
      )}
      {isExpanded && <SidebarTree parentId={doc.id} level={level + 1} section={section} />}
    </div>
  );
}